// The structured chat-error envelope LexIDE renders + logs. It carries the
// machine-readable classification + an i18n KEY (not localized copy — the
// localized strings live in each consumer's i18n catalog so copy never
// duplicates across packages). Zod-validated so the SPA can defensively parse a
// frame from an older/newer runtime.
//
// See ai-docs/lexruntime-error-handling-plan.md Pillar B.
import { z } from "zod";
import { KIND_AFFORDANCE, KIND_TONE, providerLabel, } from "./taxonomy.js";
export const ChatErrorMessageSchema = z.object({
    /** LlmRuntimeErrorKind — kept as a plain string on the wire so an older SPA
     *  never hard-fails on a kind it doesn't recognise (it falls back to the
     *  generic render). */
    kind: z.string(),
    affordance: z.enum(["auto-retry", "manual-retry", "contact-admin", "none"]),
    tone: z.enum(["auth", "transport", "info"]),
    /** Stable i18n key root; the SPA resolves `${i18nKey}.title` / `.body`. */
    i18nKey: z.string(),
    provider: z.string().optional(),
    providerLabel: z.string().optional(),
    retryAfterSeconds: z.number().optional(),
    /** Support correlation id (the run id), shown for `unknown`. */
    traceId: z.string().optional(),
    /** Observability tag — which classifier rule fired. Not user-facing. */
    matchedPattern: z.string().optional(),
});
/** Build the wire envelope from a classification. Pure; no localization. */
export function buildChatErrorMessage(classification, opts = {}) {
    const kind = classification.kind;
    const envelope = {
        kind,
        affordance: KIND_AFFORDANCE[kind],
        tone: KIND_TONE[kind],
        i18nKey: `llmErrors.llm.${kind}`,
    };
    if (classification.provider)
        envelope.provider = classification.provider;
    const label = opts.providerLabelOverride ?? providerLabel(classification.provider);
    if (label)
        envelope.providerLabel = label;
    if (typeof classification.retryAfterSeconds === "number") {
        envelope.retryAfterSeconds = classification.retryAfterSeconds;
    }
    if (opts.traceId)
        envelope.traceId = opts.traceId;
    if (classification.matchedPattern)
        envelope.matchedPattern = classification.matchedPattern;
    return envelope;
}
/** Parse a value that may be a ChatErrorMessage envelope; null on mismatch
 *  (forward/backward compatible with the flat-string error shape). */
export function parseChatErrorMessage(value) {
    const parsed = ChatErrorMessageSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=wire.js.map